import { useEffect, useState } from "react";
import { CaretLeft, CaretRight, X, ArrowsIn, ArrowsOut } from "@phosphor-icons/react";
import { API } from "@/lib/api";

/**
 * Full-screen swipeable carousel for the public portal.
 * Shows all before/after photos across all tasks (paired before → after → next task).
 * Swipe on touch, arrows on desktop, Esc to close.
 */
export default function PhotoCarousel({ open, onClose, token, tasks }) {
  const [idx, setIdx] = useState(0);

  // Build flat list of {taskId, taskLabel, slot, photoId, isBefore}
  const slides = [];
  (tasks || []).forEach((t) => {
    (t.photos_before || []).forEach((p) => slides.push({ taskId: t.id, taskLabel: t.label, slot: "before", photoId: p.id }));
    (t.photos_after || []).forEach((p) => slides.push({ taskId: t.id, taskLabel: t.label, slot: "after", photoId: p.id }));
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIdx((i) => Math.min(slides.length - 1, i + 1));
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, slides.length, onClose]);

  // Touch swipe
  const [touch, setTouch] = useState(null);
  const onTouchStart = (e) => setTouch({ x: e.touches[0].clientX, t: Date.now() });
  const onTouchEnd = (e) => {
    if (!touch) return;
    const dx = e.changedTouches[0].clientX - touch.x;
    const dt = Date.now() - touch.t;
    if (Math.abs(dx) > 50 && dt < 500) {
      if (dx < 0) setIdx((i) => Math.min(slides.length - 1, i + 1));
      else setIdx((i) => Math.max(0, i - 1));
    }
    setTouch(null);
  };

  if (!open) return null;
  if (slides.length === 0) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center text-white" data-testid="photo-carousel">
        <div className="text-center">
          <div className="text-sm text-white/60">Aucune photo à afficher.</div>
          <button onClick={onClose} className="mt-4 px-4 py-2 rounded-full border border-white/20 hover:bg-white/10" data-testid="carousel-close">Fermer</button>
        </div>
      </div>
    );
  }

  const s = slides[idx];
  const url = `${API}/public/chantiers/${token}/photo/${s.taskId}/${s.slot}/${s.photoId}`;
  const isBefore = s.slot === "before";
  const tone = isBefore ? "#F59E0B" : "#10B981";
  const label = isBefore ? "Avant" : "Après";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col select-none"
      data-testid="photo-carousel"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between p-4 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border" style={{ background: tone + "22", color: tone, borderColor: tone + "55" }}>{label}</span>
          <span className="text-sm truncate">{s.taskLabel}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono text-white/60 tabular-nums" data-testid="carousel-counter">{idx + 1} / {slides.length}</span>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center" data-testid="carousel-close" title="Fermer (Échap)">
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <img
          key={idx}
          src={url}
          alt={label}
          className="max-h-full max-w-full object-contain animate-in fade-in duration-200"
          data-testid={`carousel-slide-${idx}`}
        />
        {/* Arrows (desktop) */}
        {idx > 0 && (
          <button
            onClick={() => setIdx(idx - 1)}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 items-center justify-center active:scale-95"
            data-testid="carousel-prev"
          >
            <CaretLeft size={20} weight="bold" className="text-white" />
          </button>
        )}
        {idx < slides.length - 1 && (
          <button
            onClick={() => setIdx(idx + 1)}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 items-center justify-center active:scale-95"
            data-testid="carousel-next"
          >
            <CaretRight size={20} weight="bold" className="text-white" />
          </button>
        )}
      </div>

      {/* Thumbnails */}
      <div className="p-3 border-t border-white/10 overflow-x-auto no-scrollbar">
        <div className="flex gap-1.5">
          {slides.map((sl, i) => {
            const thumbUrl = `${API}/public/chantiers/${token}/photo/${sl.taskId}/${sl.slot}/${sl.photoId}`;
            const t = sl.slot === "before" ? "#F59E0B" : "#10B981";
            return (
              <button
                key={`${sl.taskId}-${sl.slot}-${sl.photoId}`}
                onClick={() => setIdx(i)}
                className={`shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition ${i === idx ? "opacity-100" : "opacity-40 hover:opacity-70"}`}
                style={{ borderColor: i === idx ? t : "transparent" }}
                data-testid={`carousel-thumb-${i}`}
              >
                <img src={thumbUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
